<template>
   <div
      v-if="journey || isSubmissionRoute"
      id="app"
      class="app"
   >
      <mainbar v-if="$route.name != 'Error'"></mainbar>
      <div
         id="main"
         class="main"
      >
         <router-view />
         <poweredByNxt class="powered-by"></poweredByNxt>
      </div>
      <notifications />
      <div
         v-if="journey?.background_image_url"
         id="backgroundimage"
         class="backgroundimage"
      ></div>
      <PreviewWarning v-if="isPreviewMode" />
   </div>
</template>

<script setup lang="ts">
import notifications from '@/core/components/Notifications.vue';
import check from '@/core/helpers/check';
import { useJourneyRouteParams } from '@/core/helpers/routeParams';
import poweredByNxt from '@/modules/journeys/components/poweredByNxt.vue';
import mainbar from '@/modules/mainbar/Index.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import PreviewWarning from './core/components/PreviewWarning.vue';
import { useColorScheme } from './core/composables/useColorScheme';
import { JOURNEY_ROUTE_NAMES } from './modules/journeys/routes';

const store = useStore();
const route = useRoute();

const { locale } = useI18n();

const { applyTenantColors, setBackgroundImage } = useColorScheme();

const isTestEnvironment = ref<boolean | null>(null);
const isDesktop = ref<boolean | null>(null);

// Initialize store data
store.dispatch('getLanguages');
store.dispatch('getModuleTypes');

const journey = computed(() => store.state.journeyModule.journey);
const isSubmissionRoute = computed(() => route.name === JOURNEY_ROUTE_NAMES.SUBMISSION);

const { journeyId: journeyIdRef, previewKey, previewLanguage, isPreviewMode } = useJourneyRouteParams();
const journeyId = computed(() => journeyIdRef.value || null);

function updateBackgroundImage() {
   if (isDesktop.value !== null) {
      setBackgroundImage(route.name, isDesktop.value);
   }
}

onMounted(async () => {
   isTestEnvironment.value = check.isTestEnvironment();
   isDesktop.value = check.isDesktop();
   updateBackgroundImage();
});

async function fetchJourneyById(id: string) {
   await store.dispatch('getJourneyById', id);
   if (journey.value) {
      let referralLanguage: any;
      let tenant: any = null;

      // Check for preview mode via query params

      if (!previewKey.value) {
         const referral = journey.value.referrals.find((r: any) => r.code == route.params.referral);
         referralLanguage = journey.value.languages.find((l: any) => l.id == referral?.language_id);
         tenant = referral?.team?.tenant ?? null;
      } else {
         // Preview mode: resolve theming from the journey_team pivot (preview_key always
         // exists on the team), so previews work even without a referral (e.g. in draft).
         const previewTeam = journey.value.teams.find(
            (t: any) => t.pivot?.preview_key && t.pivot.preview_key === previewKey.value,
         );
         referralLanguage = journey.value.languages.find((l: any) => l.code == previewLanguage.value);
         tenant = previewTeam?.tenant ?? null;
      }

      if (tenant) {
         if (referralLanguage) store.commit('setLanguage', referralLanguage);
         document.title = `NxtForm - ${journey.value.name[locale.value] || journey.value.name[Object.keys(journey.value.name)[0]]}`;

         await applyTenantColors(tenant);
         updateBackgroundImage();
      }
   }
}

watch(journeyId, async (newId) => {
   if (newId) {
      await fetchJourneyById(newId);
   }
});

// Update background when route changes
watch(
   () => route.name,
   () => {
      updateBackgroundImage();
   },
);

// Update background when journey changes
watch(journey, () => {
   updateBackgroundImage();
});
</script>

<style src="@nxtpeople/nxt-styles/dist/style.css"></style>

<style lang="scss">
.main {
   display: flex;
   flex-direction: column;
   width: 100%;
   position: relative;
   z-index: 2;
   justify-content: flex-start;
   height: calc(100vh - var(--header-height));
   min-height: calc(100vh - var(--header-height));
   height: calc(100dvh - var(--header-height));
   min-height: calc(100dvh - var(--header-height));
   overflow: auto;
   padding: 0 var(--nxt-gutter-large);
   @media (min-width: 769px) {
      padding: calc(var(--nxt-gutter-large) * 4);
      padding-bottom: 0;
   }
}

.backgroundimage {
   z-index: 0;
   content: '';
   position: fixed;
   margin: -5px -10px -10px -10px;
   width: 105%;
   background-repeat: repeat-x;
   background-image: var(--bg);
   background-color: var(--bgColor);
   background-size: cover;
   min-height: 105%;
   min-height: 105dvh;
   top: 0;
   left: 0;
   background-position: center;
   filter: blur(2.5px);
   -webkit-filter: blur(2.5px);

   @media (min-width: 769px) {
      opacity: 0.6;
      filter: brightness(100%) blur(1.5px);
      -webkit-filter: brightness(100%) blur(1.5px);
   }

   &-fadein {
      opacity: 1;

      @media (min-width: 769px) {
         opacity: 0.6;
      }
   }
}

.powered-by {
   margin-top: auto;
}

html,
body,
#app {
   background-color: var(--bgColor);
   font-family: var(--nxt-font-family-base);
   font-size: var(--nxt-font-large);
   color: var(--nxt-dark);
   overscroll-behavior: none;
}
</style>

